import MathUtils from '../../../utils/MathUtils';
import { safeAreaDimensions } from '../../../app/RendererPlugin';

type DynamicValue =
  | string
  | {
      value: string;
      clamp?: { min?: number; max?: number };
    };

enum Operator {
  Add = '+',
  Subtract = '-',
  Multiply = '*',
}

function isOperator(token: string): token is Operator {
  return Object.values(Operator).includes(token as Operator);
}

function makeOperation(a: number, operator: Operator, b: number): number {
  if (operator === Operator.Subtract) return a - b;
  if (operator === Operator.Multiply) return a * b;
  return a + b;
}

function parseValue(positionData: DynamicValue, width: number, height: number): number {
  const variables: Record<string, number> = {
    top: 0,
    bottom: height,
    left: 0,
    right: width,
    width,
    height,
    safewidth: safeAreaDimensions.width,
    safeheight: safeAreaDimensions.height,
  };

  let currentOperator = Operator.Add;

  const result = (typeof positionData === 'string' ? positionData : positionData.value)
    .split(' ')
    .map((token) => token.toLowerCase())
    .reduce((sum, token: string) => {
      if (isOperator(token)) {
        currentOperator = token;
        return sum;
      }

      const value: number = variables[token] ?? parseFloat(token);

      return makeOperation(sum, currentOperator, value);
    }, 0);

  const { min = -Infinity, max = Infinity } = typeof positionData === 'string' ? {} : positionData.clamp ?? {};

  return MathUtils.clamp(result, min, max);
}

type AcceptableObject = { x: number; y: number; width: number; height: number };

export type DynamicPropsData = { dynamicValues: Partial<Record<keyof AcceptableObject, DynamicValue>> };

export function processDynamicPropsObject(
  object: AcceptableObject & { data: DynamicPropsData },
  width: number,
  height: number,
): void {
  Object.keys(object.data.dynamicValues).forEach((key) => {
    if (!object.data.dynamicValues[key as keyof AcceptableObject]) return;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    object[key as keyof AcceptableObject] = parseValue(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      object.data.dynamicValues[key as keyof AcceptableObject]!,
      width,
      height,
    );
  });
}
